import { ChangeEvent, forwardRef, InputHTMLAttributes, KeyboardEvent, useCallback } from 'react';

import { TEXT_INPUT_VARIANTS, TEXT_VARIANTS } from '@pukka/common/constants';
import { tailint } from '@pukka/common/utils';
import { twMerge } from 'tailwind-merge';

import { Icon, IconNames } from '../../Icon';

const sizes = tailint({
  xl: {
    container: twMerge(TEXT_VARIANTS['title-1'].size, 'px-4 py-[15px]', 'rounded-xl'),
    content: 'h-8 leading-8',
  },
  l: {
    container: twMerge(TEXT_VARIANTS['subtitle-2'].size, 'px-4 py-[15px]', 'rounded-xl'),
    content: 'h-6 leading-6',
  },
  m: {
    container: twMerge(TEXT_VARIANTS['body-2'].size, 'px-4 py-[7px]', 'rounded-md'),
    content: 'h-6 leading-6',
  },
  s: {
    container: twMerge(TEXT_VARIANTS['body-1'].size, 'px-4 py-[7px]', 'rounded-md'),
    content: 'h-4 leading-4',
  },
});

const variants = TEXT_INPUT_VARIANTS;

const roundings = tailint({
  full: 'rounded-full',
  none: 'rounded-none',
  top: 'rounded-b-none',
  bottom: 'rounded-t-none',
  left: 'rounded-r-none',
  right: 'rounded-l-none',
});

type Variants = Exclude<keyof typeof variants, 'disabled'>;

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onSubmit'> {
  readonly icon?: IconNames;
  readonly reverse?: boolean;
  readonly variant?: Variants;
  readonly size?: keyof typeof sizes;
  readonly rounding?: keyof typeof roundings;
  readonly inputClassName?: string;
  readonly onChangeValue?: (val: string) => void;
  readonly onSubmit?: (val: string) => void;
}

const BaseInput = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    variant = 'default',
    size = 'm',
    rounding,
    icon,
    reverse,
    disabled,
    inputClassName,
    className,
    onChange,
    onChangeValue,
    onSubmit,
    onKeyUp,
    ...props
  },
  ref,
) {
  const container = twMerge(
    'flex h-fit flex-row',
    'text-neutral-900',
    'focus-ring',
    'w-full border',
    'transition-all duration-300',
    variants[variant],
    sizes[size].container,
    rounding && roundings[rounding],
    disabled && variants.disabled,
    reverse && 'flex-row-reverse',
    icon && 'items-center gap-4',
    className,
  );

  const content = twMerge(
    'block w-full text-ellipsis border-none bg-transparent p-0 outline-none placeholder:text-neutral-400 focus:ring-0',
    sizes[size].content,
    inputClassName,
  );

  const onChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      onChangeValue?.(e.currentTarget.value);
    },
    [onChange, onChangeValue],
  );

  const onKeyUpHandler = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onSubmit?.(e.currentTarget.value);
      }

      onKeyUp?.(e);
    },
    [onSubmit, onKeyUp],
  );

  return (
    <div className={container}>
      <input
        ref={ref}
        disabled={disabled}
        aria-disabled={disabled}
        {...props}
        onChange={onChangeHandler}
        onKeyUp={onKeyUpHandler}
        className={content}
      />
      {icon ? <Icon icon={icon} size="lg" className={disabled ? 'fill-neutral-300' : 'fill-neutral-400'} /> : null}
    </div>
  );
});

export { BaseInput };

export type { InputProps };
